/**
 * The reasons the API gives for refusing the price a checkout named, rather than the product.
 *
 * `POST /v2/users/products` answers `400 { error, reason }` when the price cannot be billed — see
 * the API's `checkPriceEligibility` and ADR 0002 in zl-api. Both mean the same thing to the flow:
 * the pick was made against an answer that has since moved, and it cannot be repaired here.
 */
const PRICE_REASONS = ['currency_mismatch', 'price_inactive'];

/** What the flow should do about a checkout session that could not be created. */
export type CheckoutFailureEvent = 'choiceUnavailable' | 'checkoutRefused' | 'paymentAbandoned';

/**
 * Which event a failed checkout request becomes.
 *
 * Three answers, narrowest first:
 *
 * - **`choiceUnavailable`** — the server refused the *price*. The visitor is entitled to the
 *   product; the price they named is one this customer cannot be billed at, because they picked it
 *   before there was an account to ask about and the account's currency turned out to differ (or
 *   the price has since been retired). The API used to substitute the right price silently and
 *   charge a total nobody agreed to; now it refuses and they pick again.
 * - **`checkoutRefused`** — any other 4xx. The server's answer about this *product*: they may not
 *   have it. The step normally catches this before the request, from the eligibility the product
 *   already carries; what reaches here is the race, and the offer check, which only the server can
 *   do.
 * - **`paymentAbandoned`** — anything else. A 5xx or a dropped connection may well work on a
 *   second try, so the choice stays.
 *
 * Only the first drops the pick *and* means the product list itself is out of date, which is why
 * the caller invalidates products on it: the cached list is the very thing that produced the bad
 * price, and re-answering it is what makes the step they land on correct.
 *
 * Reads both error shapes the two apps throw — zl-app's `RequestError` carries `statusCode`, the
 * educator app's `APIError` carries `response.status` — so the twins can share one decision.
 */
export const checkoutFailureEvent = (err: unknown): CheckoutFailureEvent => {
  const error = err as
    | {
        statusCode?: number;
        response?: { status?: number };
        body?: { reason?: unknown };
      }
    | undefined;

  const status = error?.response?.status ?? error?.statusCode;
  if (!status || status < 400 || status >= 500) {
    return 'paymentAbandoned';
  }

  const reason = error?.body?.reason;
  if (typeof reason === 'string' && PRICE_REASONS.includes(reason)) {
    return 'choiceUnavailable';
  }

  return 'checkoutRefused';
};
